/**
 * Utility functions for vital signs operations
 * Unit conversions, BMI calculations, and formatting
 */

import { UnitPreference, VitalValue } from '../types/vitalSigns'

/**
 * Convert temperature from Celsius to Fahrenheit
 */
export function celsiusToFahrenheit(celsius: number): number {
  return Math.round((celsius * 9) / 5 + 32)
}

/**
 * Convert temperature from Fahrenheit to Celsius
 */
export function fahrenheitToCelsius(fahrenheit: number): number {
  return Math.round(((fahrenheit - 32) * 5) / 9 * 100) / 100
}

/**
 * Convert cm to inches
 */
export function cmToInches(cm: number): number {
  return Math.round((cm / 2.54) * 100) / 100
}

/**
 * Convert inches to cm
 */
export function inchesToCm(inches: number): number {
  return Math.round(inches * 2.54 * 100) / 100
}

/**
 * Convert cm to feet and inches
 * Returns {feet, inches}
 */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalInches = cm / 2.54
  const feet = Math.floor(totalInches / 12)
  const inches = Math.round((totalInches % 12) * 10) / 10
  return { feet, inches }
}

/**
 * Convert feet and inches to cm
 */
export function feetInchesToCm(feet: number, inches: number): number {
  const totalInches = feet * 12 + inches
  return Math.round(totalInches * 2.54 * 100) / 100
}

/**
 * Convert kg to pounds
 */
export function kgToPounds(kg: number): number {
  return Math.round(kg * 2.20462 * 100) / 100
}

/**
 * Convert pounds to kg
 */
export function poundsToKg(pounds: number): number {
  return Math.round(pounds * 0.453592 * 100) / 100
}

/**
 * Convert kg to stone and pounds
 * Returns {stone, pounds}
 */
export function kgToStonePounds(kg: number): { stone: number; pounds: number } {
  const totalPounds = kg * 2.20462
  const stone = Math.floor(totalPounds / 14)
  const pounds = Math.round((totalPounds % 14) * 10) / 10
  return { stone, pounds }
}

/**
 * Convert stone and pounds to kg
 */
export function stonePoundsToKg(stone: number, pounds: number): number {
  const totalPounds = stone * 14 + pounds
  return Math.round(totalPounds * 0.453592 * 100) / 100
}

/**
 * Calculate BMI from height (cm) and weight (kg)
 */
export function calculateBMI(heightCm: number, weightKg: number): number {
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) {
    return 0
  }
  const heightM = heightCm / 100
  const bmi = weightKg / (heightM * heightM)
  return Math.round(bmi * 100) / 100
}

/**
 * Convert temperature value based on user preference
 * Returns the value in the preferred unit (but API always uses Celsius internally)
 */
export function convertTemperatureForDisplay(
  value: number,
  fromUnit: 'celsius' | 'fahrenheit',
  toUnit: 'celsius' | 'fahrenheit',
): number {
  if (fromUnit === toUnit) return value
  if (toUnit === 'fahrenheit') return celsiusToFahrenheit(value)
  return fahrenheitToCelsius(value)
}

/**
 * Convert length value based on user preference
 * API stores in cm, convert to display unit
 */
export function convertLengthForDisplay(
  value: number,
  fromUnit: 'cm' | 'in',
  toUnit: 'cm' | 'in',
): number {
  if (fromUnit === toUnit) return value
  if (toUnit === 'in') return cmToInches(value)
  return inchesToCm(value)
}

/**
 * Convert weight value based on user preference
 * API stores in kg, convert to display unit
 */
export function convertWeightForDisplay(
  value: number,
  fromUnit: 'kg' | 'lb' | 'stone',
  toUnit: 'kg' | 'lb' | 'stone',
): number {
  if (fromUnit === toUnit) return value
  if (toUnit === 'lb') return kgToPounds(value)
  if (toUnit === 'stone') {
    const { stone } = kgToStonePounds(value)
    return stone
  }
  // Converting to kg
  if (fromUnit === 'lb') return poundsToKg(value)
  if (fromUnit === 'stone') return stonePoundsToKg(value, 0) // Simplified, should pass pounds too
  return value
}

/**
 * Format temperature for display with unit
 */
export function formatTemperature(value: number, unit: 'celsius' | 'fahrenheit'): string {
  return `${value.toFixed(1)}°${unit === 'celsius' ? 'C' : 'F'}`
}

/**
 * Format height for display
 * For Imperial, shows feet'inches" format
 */
export function formatHeight(value: number, unit: 'cm' | 'in', userPreference: UnitPreference): string {
  if (unit === 'cm' && userPreference === 'imperial') {
    const { feet, inches } = cmToFeetInches(value)
    return `${feet}'${inches.toFixed(1)}"`
  }
  if (unit === 'in' && userPreference === 'metric') {
    return `${inchesToCm(value).toFixed(1)} cm`
  }
  if (unit === 'in') {
    const { feet, inches } = cmToFeetInches(inchesToCm(value))
    return `${feet}'${inches.toFixed(1)}"`
  }
  return `${value.toFixed(1)} cm`
}

/**
 * Format weight for display
 * For Imperial, shows stone.pounds format
 */
export function formatWeight(value: number, unit: 'kg' | 'lb' | 'stone', userPreference: UnitPreference): string {
  if (unit === 'kg' && userPreference === 'imperial') {
    const { stone, pounds } = kgToStonePounds(value)
    return `${stone}st ${pounds.toFixed(1)}lb`
  }
  if (unit === 'stone' && userPreference === 'metric') {
    return `${stonePoundsToKg(value, 0).toFixed(1)} kg`
  }
  if (unit === 'kg') {
    return `${value.toFixed(1)} kg`
  }
  if (unit === 'lb') {
    return `${value.toFixed(1)} lb`
  }
  const { stone, pounds } = kgToStonePounds(stonePoundsToKg(value, 0))
  return `${stone}st ${pounds.toFixed(1)}lb`
}

/**
 * Get display value for a vital value based on user preference
 * Returns the converted value in the user's preferred unit
 */
export function getDisplayValue(vitalValue: VitalValue | undefined, userPreference: UnitPreference): number | null {
  if (!vitalValue) return null

  // Temperature: API stores in celsius
  if (vitalValue.unit === 'celsius' || vitalValue.unit === 'fahrenheit') {
    if (userPreference === 'imperial') {
      return convertTemperatureForDisplay(vitalValue.value, 'celsius', 'fahrenheit')
    }
    return vitalValue.value // Already in celsius
  }

  // Length: API stores in cm
  if (vitalValue.unit === 'cm' || vitalValue.unit === 'in') {
    if (userPreference === 'imperial') {
      return convertLengthForDisplay(vitalValue.value, 'cm', 'in')
    }
    return vitalValue.value // Already in cm
  }

  // Weight: API stores in kg
  if (vitalValue.unit === 'kg' || vitalValue.unit === 'lb' || vitalValue.unit === 'stone') {
    if (userPreference === 'imperial') {
      // For display, convert to pounds (most common) or show stone
      return convertWeightForDisplay(vitalValue.value, 'kg', 'lb')
    }
    return vitalValue.value // Already in kg
  }

  // Other units (mmHg, bpm, %, etc.) - return as-is
  return vitalValue.value
}

/**
 * Get display unit for a vital value based on user preference
 */
export function getDisplayUnit(apiUnit: string, userPreference: UnitPreference): string {
  if (apiUnit === 'celsius' || apiUnit === 'fahrenheit') {
    return userPreference === 'imperial' ? 'fahrenheit' : 'celsius'
  }
  if (apiUnit === 'cm' || apiUnit === 'in') {
    return userPreference === 'imperial' ? 'in' : 'cm'
  }
  if (apiUnit === 'kg' || apiUnit === 'lb' || apiUnit === 'stone') {
    return userPreference === 'imperial' ? 'lb' : 'kg'
  }
  return apiUnit
}
